import { useMemo, useState, useCallback } from 'react';
import { Sankey, Tooltip, Rectangle, Layer } from 'recharts';
import type { SankeyNodeProps, SankeyLinkProps } from 'recharts';
import type { ColumnDef, SankeyMapping, VisualizationConfig } from '@deckspace/types';
import { getChartColors } from '../../theme/colors.js';
import { prepSankeySeries } from './utils.js';

interface Props {
  rows: Record<string, unknown>[];
  columnDefs: ColumnDef[];
  mapping: SankeyMapping;
  config: VisualizationConfig;
  width?: number;
  height?: number;
}

const SANKEY_MARGIN = { top: 10, right: 120, bottom: 10, left: 10 } as const;
const NODE_WIDTH = 20;

function SankeyNodeRenderer({
  x,
  y,
  width,
  height,
  index,
  payload,
  colors,
}: SankeyNodeProps & { colors: string[] }) {
  const fill = colors[index % colors.length];
  return (
    <Layer>
      <Rectangle x={x} y={y} width={width} height={height} fill={fill} />
      <text
        x={x + width + 8}
        y={y + height / 2}
        textAnchor="start"
        dominantBaseline="central"
        className="fill-foreground text-xs"
      >
        {payload.name}
      </text>
    </Layer>
  );
}

function SankeyLinkRenderer({
  sourceX,
  sourceY,
  sourceControlX,
  targetX,
  targetY,
  targetControlX,
  linkWidth,
  index,
  colors,
}: SankeyLinkProps & { colors: string[] }) {
  const [hovered, setHovered] = useState(false);
  const fill = colors[index % colors.length];
  const opacity = hovered ? 0.6 : 0.3;

  return (
    <Layer>
      <path
        d={`
          M${sourceX},${sourceY}
          C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}
        `}
        fill="none"
        stroke={fill}
        strokeWidth={linkWidth}
        strokeOpacity={opacity}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      />
    </Layer>
  );
}

function SankeyTooltipContent({
  active,
  payload,
}: {
  active?: boolean;
  // Recharts 3.x: link payload has source/target as SankeyNode objects
  payload?: Array<{
    payload?: {
      source?: { name?: string };
      target?: { name?: string };
      value?: number;
    };
  }>;
}) {
  if (!active || !payload || payload.length === 0) return null;
  const data = payload[0]?.payload;
  if (!data) return null;

  const sourceName = data.source?.name ?? '?';
  const targetName = data.target?.name ?? '?';
  const value = data.value ?? 0;

  return (
    <div className="bg-card border border-border rounded-md px-3 py-2 shadow-lg text-sm">
      <span className="text-foreground font-medium">
        {sourceName} &rarr; {targetName}
      </span>
      <span className="text-muted-foreground ml-2">{value.toLocaleString()}</span>
    </div>
  );
}

export function SankeyChart({ rows, columnDefs, mapping, config: _config, width, height }: Props) {
  const series = useMemo(
    () =>
      rows.length === 0
        ? { sankeyNodes: [], sankeyLinks: [] }
        : prepSankeySeries(rows, columnDefs, mapping),
    [rows, columnDefs, mapping]
  );

  const { sankeyNodes, sankeyLinks } = series;
  const nodeCount = sankeyNodes.length;
  const colors = useMemo(() => getChartColors(Math.max(nodeCount, 1)), [nodeCount]);

  const renderNode = useCallback(
    (props: SankeyNodeProps) => <SankeyNodeRenderer {...props} colors={colors} />,
    [colors]
  );

  const renderLink = useCallback(
    (props: SankeyLinkProps) => <SankeyLinkRenderer {...props} colors={colors} />,
    [colors]
  );

  if (!sankeyNodes.length) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground text-sm">
        No data available for sankey chart
      </div>
    );
  }

  return (
    <Sankey
      width={width ?? 400}
      height={height ?? 300}
      data={{ nodes: sankeyNodes, links: sankeyLinks }}
      nodeWidth={NODE_WIDTH}
      nodePadding={24}
      margin={SANKEY_MARGIN}
      node={renderNode}
      link={renderLink}
    >
      <Tooltip content={<SankeyTooltipContent />} />
    </Sankey>
  );
}
